import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Cancel,
  CheckCircle,
  EventBusy,
  AccessTime,
  CalendarMonth,
  Visibility,
  Schedule,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useBookings, useBookingStats, useUpdateBookingStatus } from '../../hooks/useBookings';
import CreateBookingModal from '../../components/bookings/CreateBookingModal';
import CancelBookingModal from '../../components/bookings/CancelBookingModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { Booking } from '../../services/bookings';
import { COLORS } from '@elevair/shared';

type StatusFilter = 'all' | 'scheduled' | 'completed' | 'cancelled' | 'no_show';
type ViewFilter = 'upcoming' | 'past' | 'all';

export default function BookingsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [view, setView] = useState<ViewFilter>('upcoming');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [noShowDialogOpen, setNoShowDialogOpen] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  // Queries
  const { data, isLoading, error } = useBookings({
    page: page + 1,
    limit: rowsPerPage,
    status: status === 'all' ? undefined : status,
    upcoming: view === 'upcoming' ? 'true' : undefined,
    sortBy: 'startTime',
    sortOrder: view === 'past' ? 'desc' : 'asc',
  });

  const { data: stats } = useBookingStats();
  const updateStatus = useUpdateBookingStatus();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, booking: Booking) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleViewBooking = () => {
    if (selectedBooking) {
      navigate(`/bookings/${selectedBooking._id}`);
    }
    handleMenuClose();
  };

  const handleMarkComplete = async () => {
    if (selectedBooking) {
      await updateStatus.mutateAsync({
        id: selectedBooking._id,
        status: 'completed'
      });
    }
    setCompleteDialogOpen(false);
    setSelectedBooking(null);
  };

  const handleMarkNoShow = async () => {
    if (selectedBooking) {
      await updateStatus.mutateAsync({
        id: selectedBooking._id,
        status: 'no_show'
      });
    }
    setNoShowDialogOpen(false);
    setSelectedBooking(null);
  };

  const statusConfig: Record<string, { color: string; icon: React.ReactNode }> = {
    scheduled: { color: '#3B82F6', icon: <Schedule fontSize="small" /> },
    completed: { color: '#10B981', icon: <CheckCircle fontSize="small" /> },
    cancelled: { color: '#EF4444', icon: <Cancel fontSize="small" /> },
    no_show: { color: '#F59E0B', icon: <EventBusy fontSize="small" /> },
    rescheduled: { color: '#8B5CF6', icon: <CalendarMonth fontSize="small" /> },
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Bookings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats?.today || 0} today - {stats?.upcoming || 0} upcoming
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<CalendarMonth />}
            onClick={() => navigate('/calendar')}
          >
            Calendar View
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateModalOpen(true)}
          >
            New Booking
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: 'Today', value: stats?.today || 0, color: COLORS.primary },
          { label: 'This Week', value: stats?.thisWeek || 0, color: '#3B82F6' },
          { label: 'Upcoming', value: stats?.upcoming || 0, color: '#10B981' },
        ].map((stat) => (
          <Card key={stat.label} sx={{ px: 3, py: 2, minWidth: 120 }}>
            <Typography variant="h5" fontWeight={600} color={stat.color}>
              {stat.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* View Tabs */}
      <Tabs
        value={view}
        onChange={(_, v) => setView(v)}
        sx={{ mb: 2 }}
      >
        <Tab label="Upcoming" value="upcoming" />
        <Tab label="Past" value="past" />
        <Tab label="All" value="all" />
      </Tabs>

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            placeholder="Search bookings..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="no_show">No Show</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load bookings. Please try again.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.bookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No bookings found.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setCreateModalOpen(true)}
                        sx={{ mt: 2 }}
                      >
                        Create Booking
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.bookings.map((booking) => (
                    <TableRow
                      key={booking._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/bookings/${booking._id}`)}
                    >
                      <TableCell>
                        <Box>
                          <Typography fontWeight={500}>
                            {format(new Date(booking.startTime), 'MMM d, yyyy')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(booking.startTime), 'h:mm a')} -
                            {format(new Date(booking.endTime), 'h:mm a')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: booking.clientId?.avatarColor || COLORS.primary,
                              fontSize: '0.875rem',
                            }}
                          >
                            {booking.clientName?.split(' ').map((n: string) => n[0]).join('')}
                          </Avatar>
                          <Typography>{booking.clientName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: booking.serviceColor,
                            }}
                          />
                          <Typography>{booking.serviceName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AccessTime fontSize="small" color="action" />
                          <Typography variant="body2">
                            {booking.duration} min
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusConfig[booking.status]?.icon as React.ReactElement}
                          label={booking.status.replace('_', ' ')}
                          size="small"
                          sx={{
                            bgcolor: `${statusConfig[booking.status]?.color}15`,
                            color: statusConfig[booking.status]?.color,
                            textTransform: 'capitalize',
                            '& .MuiChip-icon': {
                              color: 'inherit',
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, booking)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={data?.pagination.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewBooking}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {selectedBooking?.status === 'scheduled' && (
          <>
            <MenuItem onClick={() => { handleMenuClose(); setCompleteDialogOpen(true); }}>
              <CheckCircle fontSize="small" sx={{ mr: 1 }} />
              Mark Complete
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); setNoShowDialogOpen(true); }}>
              <EventBusy fontSize="small" sx={{ mr: 1 }} />
              Mark No-Show
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); setCancelModalOpen(true); }}>
              <Cancel fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
              <Typography color="error">Cancel Booking</Typography>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Create Booking Modal */}
      <CreateBookingModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />

      {/* Cancel Booking Modal */}
      <CancelBookingModal
        open={cancelModalOpen}
        booking={selectedBooking}
        onClose={() => {
          setCancelModalOpen(false);
          setSelectedBooking(null);
        }}
      />

      {/* Mark Complete Dialog */}
      <ConfirmDialog
        open={completeDialogOpen}
        title="Mark as Complete"
        message={`Mark the booking with ${selectedBooking?.clientName} as completed?`}
        confirmText="Mark Complete"
        variant="info"
        onConfirm={handleMarkComplete}
        onCancel={() => {
          setCompleteDialogOpen(false);
          setSelectedBooking(null);
        }}
        loading={updateStatus.isPending}
      />

      {/* Mark No-Show Dialog */}
      <ConfirmDialog
        open={noShowDialogOpen}
        title="Mark as No-Show"
        message={`Mark ${selectedBooking?.clientName} as a no-show for this booking?`}
        confirmText="Mark No-Show"
        variant="warning"
        onConfirm={handleMarkNoShow}
        onCancel={() => {
          setNoShowDialogOpen(false);
          setSelectedBooking(null);
        }}
        loading={updateStatus.isPending}
      />
    </Box>
  );
}
